import { useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Check, Play, X } from "lucide-react";
import CTASection from "@/components/CTASection";
import SEO from "@/components/SEO";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

// Map service slugs to portfolio categories
const SERVICE_PORTFOLIO_MAP = {
  "google-ads": [],
  "meta-advertising": ["Meta Advertising"],
  "social-media-management": ["Social Media Management"],
  "content-creation": ["Content Creation"],
  "website-design": ["Website Design"],
  "branding": ["Branding & Design"],
  "email-marketing": [],
  "seo": [],
  "google-business-profile": [],
};

// Map service slugs to case-study slugs (real Harcourts/Gareth Hookham work)
const SERVICE_CASE_MAP = {
  "google-ads": ["stihl-kapiti-google-ads", "caltex-google-ads", "cutting-lead-costs"],
  "meta-advertising": ["award-winning-agent-meta", "maximising-every-lead", "return-on-ad-spend", "better-creative-better-results", "cutting-lead-costs", "building-a-listing-pipeline", "scaling-appraisal-campaigns"],
  "social-media-management": ["building-a-listing-pipeline", "building-trust-that-sells", "driving-foot-traffic-through-social", "ford-ranger-effect"],
  "content-creation": [],
  "website-design": ["gareth-hookham-website", "shaun-bloch-website", "monique-mackay-website"],
  "branding": [],
  "email-marketing": [],
  "seo": [],
  "google-business-profile": [],
};

// Number of portfolio items to show per service page
const PORTFOLIO_LIMIT = {
  "content-creation": 20,
  "social-media-management": 20,
  "branding": 20,
  "website-design": 20,
};

// Extra bespoke showcase blocks per service (image + optional video)
const SERVICE_SHOWCASES = {
  "website-design": [
    {
      id: "gareth-hookham-showcase",
      title: "Gareth Hookham — Website",
      client: "Gareth Hookham",
      image: "/case-studies/uniform/gareth-hookham-poster.jpg",
      video: "/case-studies/uniform/gareth-hookham.mp4",
    },
  ],
};

// Map service slugs to portfolio filter category (used by "See the work" hero CTA)
const SERVICE_TO_PORTFOLIO_FILTER = {
  "meta-advertising": "Meta Advertising",
  "social-media-management": "Social Media Management",
  "content-creation": "Content Creation",
  "branding": "Branding & Design",
};

export default function ServiceDetail() {
  const { slug } = useParams();
  const [service, setService] = useState(null);
  const [caseStudies, setCaseStudies] = useState([]);
  const [portfolio, setPortfolio] = useState([]);
  const [notFound, setNotFound] = useState(false);
  const [openVideo, setOpenVideo] = useState(null);

  useEffect(() => {
    setNotFound(false);
    setService(null);
    api.get(`/services/${slug}`).then((r) => setService(r.data)).catch(() => setNotFound(true));
    api.get("/case-studies").then((r) => setCaseStudies(r.data)).catch(() => {});
    api.get("/portfolio").then((r) => setPortfolio(r.data)).catch(() => {});
  }, [slug]);

  if (notFound) {
    return (
      <div className="ob-container py-32">
        <h1 className="ob-h2">Service not found</h1>
        <Link to="/" className="ob-btn mt-8 inline-flex">Back home</Link>
      </div>
    );
  }
  if (!service) return <div className="ob-container py-32">Loading...</div>;

  const relatedSlugs = SERVICE_CASE_MAP[slug] || [];
  const relatedCases = relatedSlugs
    .map((s) => caseStudies.find((c) => c.slug === s))
    .filter(Boolean)
    .slice(0, 2);

  const relatedCats = SERVICE_PORTFOLIO_MAP[slug] || [];
  const limit = PORTFOLIO_LIMIT[slug] || 6;
  const relatedPortfolio = portfolio
    .filter((p) => relatedCats.includes(p.category))
    .slice(0, limit);

  const showcases = SERVICE_SHOWCASES[slug] || [];

  return (
    <div data-testid="service-detail-page">
      <SEO
        title={service.title}
        description={service.overview || service.tagline || undefined}
      />
      <section className="pt-16 pb-20 lg:pt-24">
        <div className="ob-container">
          <div className="text-[13px] tracking-[0.14em] uppercase mb-6" style={{ color: "var(--ob-muted)" }}>
            {service.category}
          </div>
          <h1 className="ob-h1 max-w-5xl">{service.title}</h1>
          <p className="ob-lead mt-8 max-w-3xl">{service.tagline}</p>
          <div className="mt-10 flex gap-3">
            <Link to="/contact" className="ob-btn ob-btn-primary" data-testid="service-cta">Let's have a chat</Link>
            <Link
              to={SERVICE_TO_PORTFOLIO_FILTER[slug] ? `/portfolio?category=${encodeURIComponent(SERVICE_TO_PORTFOLIO_FILTER[slug])}` : "/portfolio"}
              className="ob-btn"
              data-testid="service-see-the-work"
            >
              See the work
            </Link>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="pb-24">
        <div className="ob-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="ob-eyebrow">Overview</div>
          </div>
          <div className="lg:col-span-8">
            <p className="ob-lead">{service.overview}</p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="ob-section" style={{ background: "var(--ob-bg-alt)" }}>
        <div className="ob-container grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <div className="ob-eyebrow mb-4">Benefits</div>
            <h2 className="ob-h2">Why clients invest in {service.title.toLowerCase()}.</h2>
          </div>
          <div className="lg:col-span-8">
            <ul className="space-y-6">
              {service.benefits?.map((b, i) => (
                <li key={i} className="flex items-start gap-4 border-t border-[var(--ob-border)] pt-6">
                  <div className="w-9 h-9 rounded-full bg-white border border-[var(--ob-border)] flex items-center justify-center flex-shrink-0">
                    <Check size={16} strokeWidth={1.5} />
                  </div>
                  <div className="text-[20px] font-light" style={{ color: "var(--ob-ink)" }}>{b}</div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Includes + Process */}
      <section className="ob-section">
        <div className={`ob-container ${service.process?.length ? "grid lg:grid-cols-2 gap-14" : ""}`}>
          <div>
            <div className="ob-eyebrow mb-5">What's included</div>
            <h3 className="ob-h3 mb-8">Everything we build into a {service.title} engagement.</h3>
            <ul className="space-y-4">
              {service.includes?.map((it, i) => (
                <li key={i} className="flex items-center gap-3 py-3 border-t border-[var(--ob-border)]">
                  <span className="text-[13px] w-8" style={{ color: "var(--ob-muted)" }}>{String(i + 1).padStart(2, "0")}</span>
                  <span className="text-[17px]" style={{ color: "var(--ob-ink)" }}>{it}</span>
                </li>
              ))}
            </ul>
          </div>
          {service.process?.length > 0 && (
            <div>
              <div className="ob-eyebrow mb-5">Our process</div>
              <h3 className="ob-h3 mb-8">How we work with you.</h3>
              <ul className="space-y-6">
                {service.process.map((p, i) => (
                  <li key={i} className="grid grid-cols-[60px_1fr] gap-4 border-t border-[var(--ob-border)] pt-6">
                    <div className="text-[28px] font-light" style={{ color: "var(--ob-ink)" }}>{p.step}</div>
                    <div>
                      <div className="text-[20px] font-light" style={{ color: "var(--ob-ink)" }}>{p.title}</div>
                      <div className="ob-body mt-1">{p.detail}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Case study blocks - split card template with image + structured text */}
      {relatedCases.length > 0 && (
        <section className="ob-section" style={{ background: "var(--ob-bg-panel)" }} data-testid="service-cases">
          <div className="ob-container">
            <h2 className="ob-h2 mb-14 max-w-4xl">A closer look at some of this work in action.</h2>
            <div className="grid md:grid-cols-2 gap-8 lg:gap-10">
              {relatedCases.map((cs) => (
                <article
                  key={cs.slug}
                  data-testid={`service-case-${cs.slug}`}
                  className="bg-white rounded-3xl overflow-hidden border border-[var(--ob-border)]"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-[var(--ob-bg-alt)]">
                    {cs.video ? (
                      <video
                        src={cs.video}
                        poster={cs.image}
                        autoPlay
                        muted
                        loop
                        playsInline
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <img src={cs.image} alt={cs.client} className="w-full h-full object-cover" />
                    )}
                  </div>
                  <div className="p-6 lg:p-8">
                    <div className="ob-eyebrow mb-2">{cs.industry}</div>
                    <h3 className="ob-h3">{cs.heading || cs.client}</h3>
                    <div className="mt-6">
                      <div className="ob-eyebrow mb-1">The challenge</div>
                      <p className="ob-body">{cs.challenge}</p>
                    </div>
                    <div className="mt-5">
                      <div className="ob-eyebrow mb-1">Our solution</div>
                      <p className="ob-body">{cs.solution}</p>
                    </div>
                    <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                      {cs.results?.map((r, i) => (
                        <div key={i} className="border-t border-[var(--ob-border)] pt-3">
                          <div className="text-[22px] font-light leading-none" style={{ color: "var(--ob-ink)" }}>{r.metric}</div>
                          <div className="text-[11px] mt-2" style={{ color: "var(--ob-muted)" }}>{r.label}</div>
                        </div>
                      ))}
                    </div>
                    {cs.testimonial && (
                      <figure className="mt-6 pt-6 border-t border-[var(--ob-border)]">
                        <blockquote className="text-[17px] leading-[1.45] font-light" style={{ color: "var(--ob-ink)" }}>
                          &ldquo;{cs.testimonial.quote}&rdquo;
                        </blockquote>
                        {cs.testimonial.author && (
                          <figcaption className="mt-3 text-[13px]" style={{ color: "var(--ob-muted)" }}>
                            <span style={{ color: "var(--ob-ink)" }}>{cs.testimonial.author}</span>
                          </figcaption>
                        )}
                      </figure>
                    )}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Portfolio relevant to this service */}
      {relatedPortfolio.length > 0 && (
        <section className="ob-section" data-testid="service-portfolio">
          <div className="ob-container">
            <div className="max-w-3xl mb-14">
              <h2 className="ob-h2">Some {service.title.toLowerCase()} work.</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {relatedPortfolio.map((p) => (
                <PortfolioCard
                  key={p.id}
                  item={p}
                  onOpenVideo={() => setOpenVideo(p)}
                  hideMeta={true}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bespoke showcases (e.g. Boostique on website-design) */}
      {showcases.length > 0 && (
        <section className="ob-section" style={{ background: "var(--ob-bg-panel)" }} data-testid="service-showcases">
          <div className="ob-container">
            <div className="max-w-3xl mb-14">
              <h2 className="ob-h2">A closer look at some of this work in action.</h2>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {showcases.map((sc) => (
                <PortfolioCard key={sc.id} item={sc} onOpenVideo={() => setOpenVideo(sc)} hideMeta={true} />
              ))}
            </div>
          </div>
        </section>
      )}

      <VideoDialog item={openVideo} onOpenChange={(o) => !o && setOpenVideo(null)} />

      {/* FAQ */}
      {service.faqs?.length > 0 && (
        <section className="ob-section" style={{ background: "var(--ob-bg-alt)" }}>
          <div className="ob-container grid lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <div className="ob-eyebrow mb-4">FAQs</div>
              <h2 className="ob-h2">Common questions.</h2>
            </div>
            <div className="lg:col-span-8">
              <Accordion type="single" collapsible className="w-full">
                {service.faqs.map((f, i) => (
                  <AccordionItem key={i} value={`item-${i}`} className="border-b border-[var(--ob-border)]">
                    <AccordionTrigger className="text-left text-[20px] font-light py-6 hover:no-underline" data-testid={`faq-${i}`}>
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-[17px] pb-6 opacity-80" style={{ color: "var(--ob-ink-soft)" }}>
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </div>
          </div>
        </section>
      )}

      <CTASection />
    </div>
  );
}


function PortfolioCard({ item, onOpenVideo, hideMeta = false }) {
  const isVideo = Boolean(item.video);
  const inner = (
    <div className="relative aspect-[4/5] rounded-2xl overflow-hidden bg-[var(--ob-bg-alt)] mb-4">
      {isVideo ? (
        <video
          src={item.video}
          poster={item.image}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="w-full h-full object-cover"
        />
      ) : (
        <img
          src={item.image}
          alt={item.title}
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-700"
        />
      )}
      {isVideo && (
        <>
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/25 transition-colors duration-300 pointer-events-none" />
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="h-14 w-14 lg:h-16 lg:w-16 rounded-full bg-white/95 flex items-center justify-center shadow-lg opacity-90 group-hover:opacity-100 group-hover:scale-110 transition-all duration-300">
              <Play size={22} strokeWidth={1.75} className="translate-x-[2px]" style={{ color: "var(--ob-ink)" }} />
            </div>
          </div>
        </>
      )}
    </div>
  );

  const meta = hideMeta ? null : (
    <>
      <div className="ob-eyebrow mb-1">{item.category || item.client}</div>
      <div className="text-[18px] font-light" style={{ color: "var(--ob-ink)" }}>
        {item.title}
      </div>
    </>
  );

  if (isVideo) {
    return (
      <button
        type="button"
        onClick={onOpenVideo}
        aria-label={`Play video: ${item.title}`}
        data-testid={`service-portfolio-${item.id}`}
        className="group text-left w-full"
      >
        {inner}
        {meta}
      </button>
    );
  }
  return (
    <div className="group" data-testid={`service-portfolio-${item.id}`}>
      {inner}
      {meta}
    </div>
  );
}

function VideoDialog({ item, onOpenChange }) {
  const videoRef = useRef(null);
  const open = Boolean(item);

  useEffect(() => {
    if (open && videoRef.current) {
      videoRef.current.currentTime = 0;
      videoRef.current.muted = false;
      videoRef.current.play().catch(() => {
        if (videoRef.current) {
          videoRef.current.muted = true;
          videoRef.current.play().catch(() => {});
        }
      });
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-5xl w-[95vw] p-0 gap-0 border-0 bg-black !rounded-2xl overflow-hidden [&>button.absolute]:hidden"
        data-testid="video-dialog"
      >
        <DialogTitle className="sr-only">{item?.title || "Portfolio video"}</DialogTitle>
        <div className="relative">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close video"
            data-testid="video-dialog-close"
            className="absolute top-3 right-3 z-20 h-10 w-10 rounded-full bg-white/95 hover:bg-white flex items-center justify-center transition-colors"
          >
            <X size={18} strokeWidth={1.75} style={{ color: "#111" }} />
          </button>
          {item && (
            <div className="w-full aspect-video bg-black">
              <video
                ref={videoRef}
                src={item.video}
                poster={item.image}
                controls
                playsInline
                className="w-full h-full object-contain bg-black"
              />
            </div>
          )}
        </div>
        {item && (
          <div className="px-6 py-5 bg-white flex items-center justify-between gap-4">
            <div>
              <div className="ob-eyebrow mb-1">{item.category || item.client}</div>
              <div className="text-[18px] font-light leading-tight" style={{ color: "var(--ob-ink)" }}>
                {item.title}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
